import { userProfileDataSchema, type UserProfileDataType } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useEffect, useState } from 'react'
import { Alert, View } from 'react-native'
import ContentBox, { BoxActionsHeader } from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { ViewTitle } from '~/components/layout/Sections'
import { ScrollView } from '~/components/ScrollView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { queryClient, trpc, trpcClient } from '~/lib/trpc'
import { cn } from '~/lib/utils'

type OrganisationUser = {
  user_id: string
  profile_id: string
  name: string
  slug: string
  email: string
  data: UserProfileDataType | null
}

function UserRow({ user }: { user: OrganisationUser }) {
  return (
    <View className="flex-row items-center gap-4 rounded-lg border border-neutral-200 bg-white p-4">
      <Avatar className="size-10" alt={user.name}>
        {user.data?.avatar_url ? (
          <AvatarImage source={{ uri: user.data.avatar_url }} />
        ) : (
          <AvatarFallback>
            <Text className="font-semibold text-sm">
              {user.name?.charAt(0)?.toUpperCase() ?? '?'}
            </Text>
          </AvatarFallback>
        )}
      </Avatar>
      <View className="flex-1 gap-1">
        <Text className="font-medium text-base">{user.name}</Text>
        <Label className="text-sm text-secondary">{user.email}</Label>
      </View>
    </View>
  )
}

export default function OrganisationDetail() {
  const { t } = useTranslations(['admin', 'common'])
  const { organisationId } = useLocalSearchParams<{ organisationId: string }>()
  const router = useRouter()

  const { data, isLoading, error } = useQuery(
    trpc.admin.getOrganisation.queryOptions({ organisationId: organisationId! })
  )

  const [name, setName] = useState('')
  const [emailDomain, setEmailDomain] = useState('')

  useEffect(() => {
    if (data?.organisation) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
      setName(data.organisation.name)
      setEmailDomain(data.organisation.email_domain)
    }
  }, [data])

  const updateMutation = useMutation({
    mutationFn: async () => {
      return await trpcClient.admin.updateOrganisation.mutate({
        organisationId: organisationId!,
        name,
        emailDomain,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'listOrganisations'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'getOrganisation', { organisationId }],
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await trpcClient.admin.deleteOrganisation.mutate({
        organisationId: organisationId!,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'listOrganisations'],
      })
      router.replace('/(authenticated)/admin/organisations')
    },
  })

  const handleDelete = () => {
    Alert.alert(
      t(($) => $.organisations.edit.deleteConfirmTitle),
      t(($) => $.organisations.edit.deleteConfirmMessage, { name }),
      [
        { text: t(($) => $.common.actions.cancel), style: 'cancel' },
        {
          text: t(($) => $.common.actions.delete),
          style: 'destructive',
          onPress: () => deleteMutation.mutate(),
        },
      ]
    )
  }

  const hasChanges =
    data?.organisation &&
    (name !== data.organisation.name || emailDomain !== data.organisation.email_domain)

  if (!organisationId) {
    return null
  }

  return (
    <ScreenView>
      <ScrollView
        className={cn('flex-1 self-stretch rounded-content transition-opacity duration-150')}
        offsetTrackHeight
        contentContainerClassName="gap-3">
        <ContentBox contentContainerClassName="gap-6" variant="transparent">
          {isLoading && (
            <Body className="text-center text-neutral-500">
              {t(($) => $.organisations.edit.loading)}
            </Body>
          )}

          {!!error && (
            <Body className="text-center text-destructive">
              {t(($) => $.organisations.edit.loadError)}
            </Body>
          )}

          {data && (
            <>
              <View className="gap-4">
                <Heading2 className="text-center">{t(($) => $.organisations.edit.title)}</Heading2>
              </View>

              <View className="gap-6">
                <View className="gap-2">
                  <Label>{t(($) => $.organisations.edit.nameLabel)}</Label>
                  <Input
                    value={name}
                    onChangeText={setName}
                    placeholder={t(($) => $.organisations.edit.namePlaceholder)}
                    variant="box"
                    className={cn('bg-white', !name.trim() && 'border-destructive/70')}
                  />
                </View>

                <View className="gap-2">
                  <Label>{t(($) => $.organisations.edit.emailDomainLabel)}</Label>
                  <Input
                    value={emailDomain}
                    onChangeText={(text) => setEmailDomain(text.toLowerCase().trim())}
                    placeholder={t(($) => $.organisations.edit.emailDomainPlaceholder)}
                    variant="box"
                    autoCapitalize="none"
                    autoCorrect={false}
                    className={cn('bg-white', !emailDomain.trim() && 'border-destructive/70')}
                  />
                </View>

                <View className="flex-row gap-3">
                  <Button
                    variant="primary"
                    size="lg"
                    className="flex-1"
                    disabled={!hasChanges || updateMutation.isPending}
                    onPress={() => updateMutation.mutate()}>
                    <ButtonLabel>
                      {updateMutation.isPending
                        ? t(($) => $.organisations.edit.saving)
                        : t(($) => $.organisations.edit.save)}
                    </ButtonLabel>
                  </Button>
                </View>

                {!!updateMutation.error && (
                  <Body className="text-center text-destructive">
                    {t(($) => $.organisations.edit.saveError)}
                  </Body>
                )}
              </View>
            </>
          )}
        </ContentBox>

        {data && (
          <ContentBox
            scrollable={false}
            contentContainerClassName="gap-4 px-0 pt-0"
            header={
              <BoxActionsHeader variant="default">
                <ViewTitle>
                  {t(($) => $.organisations.edit.usersTitle, {
                    count: data.users.length,
                  })}
                </ViewTitle>
              </BoxActionsHeader>
            }>
            {data.users.length === 0 ? (
              <Body className="px-6 text-center text-secondary">
                {t(($) => $.organisations.edit.noUsers)}
              </Body>
            ) : (
              <View className="gap-3 px-6">
                {data.users.map((user) => {
                  const parsedUser = userProfileDataSchema.safeParse(user.data)

                  if (!parsedUser.success) {
                    return null
                  }

                  return <UserRow key={user.user_id} user={{ ...user, data: parsedUser.data }} />
                })}
              </View>
            )}
          </ContentBox>
        )}

        {data && (
          <ContentBox
            scrollable={false}
            contentContainerClassName="gap-4 px-0 pt-0"
            header={
              <BoxActionsHeader variant="default">
                <ViewTitle>{t(($) => $.organisations.edit.dangerousActions)}</ViewTitle>
              </BoxActionsHeader>
            }>
            <View className="gap-4 px-6">
              <Body className="text-sm text-secondary">
                {t(($) => $.organisations.edit.deleteWarning)}
              </Body>
              <Button
                variant="destructive"
                size="lg"
                disabled={deleteMutation.isPending}
                onPress={handleDelete}>
                <ButtonLabel>
                  {deleteMutation.isPending
                    ? t(($) => $.organisations.edit.deleteButtonLoading)
                    : t(($) => $.organisations.edit.deleteButton)}
                </ButtonLabel>
              </Button>
            </View>
          </ContentBox>
        )}
      </ScrollView>
    </ScreenView>
  )
}
